import { isEqual } from 'lodash-es';
import pDebounce from 'p-debounce';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { useState } from 'react';
import type { MultiValue as MultiValueType } from 'react-select';
import AsyncSelectComponent from 'react-select/async';

import { SEARCH_DEBOUNCE_DELAY } from '@zen/utils/constants';
import { useHover } from '@zen/utils/hooks/useHover';

import type { LabelPlacement } from '../../Label';
import Label from '../../Label';
import {
  ClearIndicator,
  getMenuList,
  getMultiValue,
  getNoOptionsMessage,
  Option as OptionComponent,
  ValueContainer
} from '../components';
import DropdownIndicator from '../components/DropdownIndicator';
import LoadingMessage from '../components/LoadingMessage';
import { asyncMultiSelectStyles, customTheme } from '../select-styles';
import type { CommonProps, SelectVariant } from '../types';

interface Props<Value> extends CommonProps {
  cacheOptions?: boolean;
  defaultOptions?: boolean | Value[];
  formatOptionLabel: (value: Value, inputValue: string, isSelected: boolean, context: 'menu' | 'value') => ReactNode;
  isOptionDisabled?: (option: Value) => boolean;
  label?: ReactNode;
  labelPlacement?: LabelPlacement;
  loadOptions: (inputValue: string) => Promise<Value[]>;
  noOptionsMessage?: ReactNode;
  onChange?: (value: Value[]) => void;
  onInputChange?: (inputValue: string) => void;
  optionKey?: keyof Value;
  renderMenuInPortal?: boolean;
  size?: 'default' | 'compact';
  value?: Value[];
  variant?: SelectVariant;
}

const AsyncMultiSelect = <ResultType extends { label: string }>(props: Props<ResultType>): ReactElement => {
  const {
    autoFocus,
    cacheOptions = true,
    defaultOptions,
    dropdownFooterRenderer,
    noOptionsMessage,
    className,
    hasError,
    hideSelectedOptions,
    inputValue,
    isClearable,
    isDisabled,
    isLoading,
    isOptionDisabled,
    label,
    isSearchable = true,
    labelPlacement,
    loadOptions,
    name,
    onBlur,
    onChange,
    onInputChange,
    optionKey,
    placeholder = 'Search...',
    renderMenuInPortal = false,
    size = 'default',
    suggestion,
    variant = 'default',
    value = []
  } = props;

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [ref, isHovered]: [RefObject<HTMLInputElement | null>, boolean] = useHover();

  const onInputValueChange = (input: string): void => {
    setIsMenuOpen(input.length > 0);

    onInputChange?.(input);
  };

  const handleFocus = (): void => setIsMenuOpen(true);

  const debouncedHandleInputChange: (inputValue: string) => Promise<ResultType[]> = pDebounce(loadOptions, SEARCH_DEBOUNCE_DELAY);

  const isOptionSelected = (selectedOption: ResultType, options: readonly ResultType[]): boolean => {
    return !!options.find((option) => {
      if (optionKey) {
        return selectedOption[optionKey] === option[optionKey];
      }

      return isEqual(selectedOption, option);
    });
  };

  const formatLabel = (
    option: ResultType,
    {
      context,
      inputValue: input,
      selectValue
    }: { context: 'menu' | 'value'; inputValue: string; selectValue: readonly ResultType[] }
  ): ReactNode => {
    const isSelected: boolean = isOptionSelected(option, selectValue);

    return props.formatOptionLabel(option, input, isSelected, context);
  };

  const handleChange = (selectedOptions: MultiValueType<ResultType>): void => {
    onChange?.(selectedOptions ? [...selectedOptions] : []);
  };

  const shouldEnableBlurOnSelect: boolean = variant !== 'default';
  const asyncMultiSelect: ReactElement = (
    <div ref={ref} className="flex-1">
      <AsyncSelectComponent<ResultType, true>
        autoFocus={autoFocus}
        blurInputOnSelect={shouldEnableBlurOnSelect}
        cacheOptions={cacheOptions}
        className={className}
        classNames={{ option: () => 'async-select-option' }}
        closeMenuOnSelect={false}
        components={{
          DropdownIndicator,
          ...(variant === 'default' ? { ValueContainer } : {}),
          MenuList: getMenuList(dropdownFooterRenderer),
          ...(!noOptionsMessage ? { NoOptionsMessage: getNoOptionsMessage(suggestion, true) } : {}),
          ClearIndicator,
          MultiValue: getMultiValue<ResultType, true>(),
          Option: OptionComponent
        }}
        defaultOptions={defaultOptions}
        formatOptionLabel={formatLabel}
        hideSelectedOptions={hideSelectedOptions}
        inputId={name}
        inputValue={inputValue}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isMulti={true}
        isOptionDisabled={isOptionDisabled}
        isOptionSelected={isOptionSelected}
        isSearchable={isSearchable}
        loadingMessage={LoadingMessage}
        loadOptions={debouncedHandleInputChange}
        menuIsOpen={isMenuOpen}
        menuPlacement="auto"
        name={name}
        noOptionsMessage={() => noOptionsMessage}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={defaultOptions ? handleFocus : undefined}
        onInputChange={onInputValueChange}
        placeholder={placeholder}
        styles={asyncMultiSelectStyles({ hasError, variant, isHovered, isCompactSize: size === 'compact' })}
        theme={customTheme}
        value={value}
        {...(renderMenuInPortal ? { menuPortalTarget: document.body } : {})}
      />
    </div>
  );

  if (!label) {
    return asyncMultiSelect;
  }

  return (
    <Label content={label} labelPlacement={labelPlacement} name={name || 'select'} variant={variant}>
      {asyncMultiSelect}
    </Label>
  );
};

export type { Props as AsyncMultiSelectProps };

export default AsyncMultiSelect;
